import { Helmet } from 'react-helmet-async';
import { env } from '@/lib/env';

export type HelmetIconVariant = 'default' | 'staging' | 'dev';

type Props = {
  /**
   * Override the favicon variant. When omitted, the variant is auto-detected
   * from the build environment:
   *   - `import.meta.env.MODE === 'development'` → `'dev'`
   *   - `import.meta.env.VITE_APP_ENV === 'staging'` → `'staging'`
   *   - otherwise → `'default'`
   */
  variant?: HelmetIconVariant;
};

// NOTE: staging/dev favicons are not yet shipped in `apps/web/public/`.
// Until they are added, every variant resolves to the production favicon.
// Drop `favicon-staging.ico` and `favicon-dev.ico` into `apps/web/public/`
// to flip the alternates on; no other changes required.
const FAVICON_BY_VARIANT: Record<HelmetIconVariant, string> = {
  default: '/favicon.ico',
  staging: '/favicon.ico',
  dev: '/favicon.ico',
};

function detectVariant(): HelmetIconVariant {
  if (import.meta.env.MODE === 'development') return 'dev';
  if (env.appEnv === 'staging') return 'staging';
  return 'default';
}

/**
 * Injects a `<link rel="icon">` into `document.head` via `react-helmet-async`.
 *
 * Mount once at the root of the React tree (above the router) so the favicon
 * is set on every route. The `<HelmetProvider>` boundary must wrap this
 * component for the helmet to flush — it lives in `main.tsx`.
 */
export function HelmetIcon({ variant }: Props) {
  const resolved = variant ?? detectVariant();
  const href = FAVICON_BY_VARIANT[resolved];

  return (
    <Helmet>
      <link rel="icon" href={href} />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
    </Helmet>
  );
}
